package presentation.screens.person

import analytics.Analytics
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.viewModelScope
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.model.Route
import crashreporting.CrashReporter
import data.VideoRepository
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.launch
import org.koin.core.component.KoinComponent
import presentation.screens.allVideos.AllVideosState
import utils.BaseViewModel

class PersonViewModel(
    private val repository: VideoRepository,
    private val savedStateHandle: SavedStateHandle,
    private val navigator: Navigator,
    private val analytics: Analytics,
    private val crashReporter: CrashReporter,
) : BaseViewModel<PersonState, PersonMVIAction>(), KoinComponent {

    private val personId = savedStateHandle.toRoute<Route.Dialog.Person>().personId

    private val coroutineExceptionHandler = CoroutineExceptionHandler { _, error ->
        updateState { copy(isLoading = false, isError = true) }
        crashReporter.capture(error)
    }

    override fun initState(): PersonState = PersonState()

    init {
        loadPerson()
    }

    override fun onAction(action: PersonMVIAction) {
        when (action) {
            is PersonMVIAction.Reload -> {
                updateState { copy(isLoading = true, isError = false) }
                loadPerson()
            }

            is PersonMVIAction.BackClicked -> navigator.back()

            is PersonMVIAction.ToggleBiography -> updateState {
                copy(isFullBiography = !isFullBiography)
            }

            is PersonMVIAction.OpenFilmography -> {
                val person = state.value.person ?: return
                navigator.dismissModalAndNavigate(
                    Route.AllVideo(
                        screenType = AllVideosState.ScreenType.ByActor.name,
                        title = person.name.orEmpty(),
                        genre = "",
                        personId = person.id,
                    )
                )
            }

            is PersonMVIAction.OnVideoClicked -> navigator.navigateTo(
                Route.Dialog.Details(action.id, action.isTvShow)
            )
        }
    }

    private fun loadPerson() {
        viewModelScope.launch(coroutineExceptionHandler) {
            updateState { copy(isLoading = true, isError = false) }
            val person = repository.getPersonDetails(personId)
            if (person == null) {
                updateState { copy(isLoading = false, isError = true) }
                return@launch
            }
            analytics.track(AppEvents.personOpened(person.id))
            updateState {
                copy(
                    isLoading = false,
                    isError = false,
                    person = person,
                )
            }
        }
    }
}
